'use client';

/**
 * HistorySidebar — desktop-docked conversation history sidebar (v3 T6/T8).
 * Rendered by page.tsx alongside the existing conversation column at `lg:`
 * and up; hidden entirely below `lg:` (mobile uses the full-screen
 * ConversationHistoryDrawer instead — see conversation-header.tsx).
 *
 * Owns its own data (conversation list fetch, search, delete) and identity
 * store reads internally per the design brief — the only external inputs
 * are the collapsed/expanded UI state and the "new chat" action, both of
 * which page.tsx already owns (collapsed persists to localStorage there).
 */

import { memo, useCallback, useMemo, useState } from 'react';
import {
  Clock,
  Plus,
  Search,
  PanelLeftClose,
  PanelLeftOpen,
} from 'lucide-react';
import { cn } from '@/lib/utils';
import { V3_COLORS } from '@/lib/design-tokens';
import { useChatContext } from '@/contexts/ChatContext';
import { useConversationStore } from '@/stores/conversation-store';
import {
  useConversationList,
  useDeleteConversation,
} from '@/hooks/useConversations';
import {
  injectActiveConversation,
  applyPreviewCache,
} from './conversation-history/utils';
import { useConversationPreviews } from './conversation-history/use-conversation-previews';
import { HistoryList } from './conversation-history/history-list';
import { DeviceBanner } from './device-banner';
import { IdentityFooter } from './identity-footer';
import { OrbAvatar } from './primitives/orb-avatar';
import { IdentityAvatar } from './primitives/identity-avatar';
import { ExpandingCapsule, CAPSULE_SIZE } from './primitives/expanding-capsule';

const WIDTH_OPEN = 312;
const WIDTH_COLLAPSED = 60;
const ICON_COLOR = '#9a8f7f';

export interface HistorySidebarProps {
  collapsed: boolean;
  onToggleCollapsed: () => void;
  onNewChat: () => void;
  className?: string;
}

export const HistorySidebar = memo(function HistorySidebar({
  collapsed,
  onToggleCollapsed,
  onNewChat,
  className,
}: HistorySidebarProps) {
  // Only fetch while expanded — the collapsed rail shows no per-item data.
  // isPending = FIRST fetch, no cached data yet → skeleton rows (background
  // refetches keep the cached list up instead).
  const { data, isPending, isError, refetch } = useConversationList(
    50,
    0,
    !collapsed
  );
  const { mutate: deleteConversation } = useDeleteConversation();

  const conversationId = useConversationStore((s) => s.conversationId);
  const activeConversationTitle = useConversationStore(
    (s) => s.activeConversationTitle
  );
  const resumeConversation = useConversationStore((s) => s.resumeConversation);

  const [searchQuery, setSearchQuery] = useState('');
  const { messages } = useChatContext();

  // Session preview cache: live snippet from the active transcript, plus the
  // last-known snippet of every conversation visited this session — keeps
  // preview lines stable while a resumed conversation is loading.
  const previews = useConversationPreviews(messages);

  // Inject the active conversation so an in-progress chat (no backend row
  // yet) shows immediately (#83) — same convention as the popover/drawer.
  const conversations = useMemo(
    () =>
      injectActiveConversation(
        applyPreviewCache(data?.conversations ?? [], previews),
        conversationId,
        activeConversationTitle,
        new Date().toISOString(),
        previews[conversationId] ?? null
      ),
    [data, conversationId, activeConversationTitle, previews]
  );
  const isEmpty = conversations.length === 0;

  const handleSelect = useCallback(
    (id: string, title: string | null) => {
      // Re-selecting the already-open conversation must not tear down and
      // reload it — the row click is a no-op when it's already active.
      if (id === conversationId) return;
      resumeConversation(id, title ?? undefined);
    },
    [conversationId, resumeConversation]
  );

  const handleDelete = useCallback(
    (id: string, isLocalOnly: boolean) => {
      if (isLocalOnly) {
        // The injected active-conversation row — no server record exists yet
        // (see injectActiveConversation), so there's nothing to DELETE. This
        // is a local reset, same path as "New chat".
        onNewChat();
        return;
      }
      deleteConversation(id);
      // Deleting the OPEN conversation must also leave it: the active id is
      // re-injected as a synthetic row (injectActiveConversation), so without
      // a reset the "deleted" row lingers until another chat is selected.
      if (id === conversationId) onNewChat();
    },
    [deleteConversation, conversationId, onNewChat]
  );

  return (
    <aside
      className={cn(
        'flex h-full flex-col overflow-hidden border-r transition-[width] duration-300',
        className
      )}
      style={{
        width: collapsed ? WIDTH_COLLAPSED : WIDTH_OPEN,
        backgroundColor: collapsed ? undefined : V3_COLORS.surfaceSidebar,
        borderColor: V3_COLORS.warmBorderAlt,
      }}
    >
      {collapsed ? (
        <CollapsedRail
          onToggleCollapsed={onToggleCollapsed}
          onNewChat={onNewChat}
        />
      ) : (
        <>
          {/* Top row: brand + collapse toggle */}
          <div className="flex shrink-0 items-center justify-between px-[16px] pb-[12px] pt-[18px]">
            <div className="flex min-w-0 items-center gap-2">
              <OrbAvatar size={26} />
              <span
                className="truncate font-[family-name:var(--font-lora)] text-[16px] font-semibold"
                style={{ color: V3_COLORS.ink }}
              >
                Art of Living
              </span>
            </div>
            <button
              onClick={onToggleCollapsed}
              aria-label="Collapse sidebar"
              // Color transition merged with the shared press feel's
              // transform (see PRESSABLE's doc comment in design-tokens.ts).
              className="flex size-7 shrink-0 items-center justify-center rounded-md transition-[background-color,transform] duration-[160ms] ease-out hover:bg-black/5 motion-safe:active:scale-[0.96]"
              style={{ color: ICON_COLOR }}
            >
              <PanelLeftClose size={18} />
            </button>
          </div>

          {/* While the first fetch is in flight, fall through to the list
              branch (search + skeleton rows) — flashing the big "No
              conversations yet" empty state before data arrives read as the
              history being gone for a beat. */}
          {isEmpty && !isPending ? (
            <>
              <div className="px-[12px] pb-[12px]">
                <button
                  onClick={onNewChat}
                  // Color transition merged with the shared press feel's
                  // transform (see PRESSABLE's doc comment in design-tokens.ts).
                  className="flex h-11 w-full items-center justify-center gap-2 rounded-[22px] text-[14.5px] font-semibold text-white transition-[background-color,transform] duration-[160ms] ease-out motion-safe:active:scale-[0.96]"
                  style={{ backgroundColor: V3_COLORS.orange }}
                >
                  <Plus size={18} />
                  New conversation
                </button>
              </div>
              <div className="flex flex-1 flex-col items-center justify-center gap-[12px] px-6 pb-6 text-center">
                <div
                  className="flex items-center justify-center rounded-full"
                  style={{ boxShadow: '0 0 0 6px rgba(255,183,74,.12)' }}
                >
                  <OrbAvatar size={46} />
                </div>
                <div className="flex flex-col gap-1">
                  <div
                    className="font-[family-name:var(--font-lora)] text-[16px] font-semibold"
                    style={{ color: V3_COLORS.ink }}
                  >
                    No conversations yet
                  </div>
                  <div
                    className="text-[13px]"
                    style={{ color: V3_COLORS.muted }}
                  >
                    Your new chats will show up here.
                  </div>
                </div>
              </div>
            </>
          ) : (
            <>
              <div className="flex shrink-0 items-center gap-2.5 px-[12px]">
                <div className="relative min-w-0 flex-1">
                  <Search
                    aria-hidden="true"
                    size={15}
                    className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2"
                    style={{ color: '#b0a48f' }}
                  />
                  <input
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    placeholder="Search conversations"
                    className="h-9 w-full rounded-[20px] border-[1px] bg-white pl-9 pr-[12px] text-[13.5px] outline-none placeholder:text-[#b3a996]"
                    style={{ borderColor: V3_COLORS.warmBorderAlt }}
                  />
                </div>
                <ExpandingCapsule
                  size={CAPSULE_SIZE.SM}
                  label="New"
                  onClick={onNewChat}
                />
              </div>

              <DeviceBanner />

              <HistoryList
                conversations={conversations}
                activeConversationId={conversationId}
                searchQuery={searchQuery}
                onSelect={handleSelect}
                onDelete={handleDelete}
                isPending={isPending}
                isError={isError}
                onRetry={refetch}
                className="flex-1 px-2 pb-2 pt-1"
              />
            </>
          )}

          <IdentityFooter />
        </>
      )}
    </aside>
  );
});

// ── Collapsed icon rail ──────────────────────────────────────────

function CollapsedRail({
  onToggleCollapsed,
  onNewChat,
}: {
  onToggleCollapsed: () => void;
  onNewChat: () => void;
}) {
  return (
    <div className="flex h-full flex-col items-center gap-3.5 pt-[16px]">
      <button
        onClick={onToggleCollapsed}
        aria-label="Expand sidebar"
        // Color transition merged with the shared press feel's transform
        // (see PRESSABLE's doc comment in design-tokens.ts).
        className="flex size-8 items-center justify-center rounded-md transition-[background-color,transform] duration-[160ms] ease-out hover:bg-black/5 motion-safe:active:scale-[0.96]"
        style={{ color: ICON_COLOR }}
      >
        <PanelLeftOpen size={18} />
      </button>

      <button
        onClick={onNewChat}
        aria-label="New conversation"
        // hover:scale-105 predates the shared press feel and stays (a
        // distinct hover affordance); the press itself is standardized to
        // PRESSABLE's duration/scale rather than its own prior ad hoc values.
        className="flex h-[34px] w-11 items-center justify-center rounded-[17px] text-white transition-transform duration-[160ms] ease-out hover:scale-105 motion-safe:active:scale-[0.96]"
        style={{ backgroundColor: V3_COLORS.orange }}
      >
        <Plus size={18} />
      </button>

      {/* Search/history icons just expand the sidebar — no standalone
          collapsed-rail search or list UI per spec. */}
      <button
        onClick={onToggleCollapsed}
        aria-label="Search conversations"
        // Color transition merged with the shared press feel's transform
        // (see PRESSABLE's doc comment in design-tokens.ts).
        className="flex size-8 items-center justify-center rounded-md transition-[background-color,transform] duration-[160ms] ease-out hover:bg-black/5 motion-safe:active:scale-[0.96]"
        style={{ color: ICON_COLOR }}
      >
        <Search size={18} />
      </button>
      <button
        onClick={onToggleCollapsed}
        aria-label="Conversation history"
        // Color transition merged with the shared press feel's transform
        // (see PRESSABLE's doc comment in design-tokens.ts).
        className="flex size-8 items-center justify-center rounded-md transition-[background-color,transform] duration-[160ms] ease-out hover:bg-black/5 motion-safe:active:scale-[0.96]"
        style={{ color: ICON_COLOR }}
      >
        <Clock size={18} />
      </button>

      <div className="mt-auto pb-[16px]">
        <IdentityAvatar initials="G" size={34} />
      </div>
    </div>
  );
}
